import type { IDetectMetricResult } from '~/modules/analytics/types';
import type { IGetTextPrice } from '~/modules/shopify';
import type { IAnalyticMetric } from '~/types/metric';

import { useCampaignAnalyticMetricFunc } from './useCampaignAnalyticMetricFunc';
import { formatMetricByFormatter } from '~/modules/analytics';
import { useConvertMoneyByCurrency } from '~/modules/shopify';
import { useCallback, useMemo } from 'react';

export interface ICampaignAnalyticMetricDetectProps {
  metric?: IAnalyticMetric;
}
export type ICampaignAnalyticMetricDetectKey =
  | 'rpvMetric'
  | 'aovMetric'
  | 'atcRateMetric'
  | 'linkCtrMetric'
  | 'defaultMetric'
  | 'bounceRateMetric'
  | 'avgTimeOnPageMetric'
  | 'conversionRateMetric'
  | 'totalATCMetric'
  | 'totalViewMetric'
  | 'totalOrderMetric'
  | 'totalSessionMetric'
  | 'totalRevenueMetric'
  | 'totalVisitorMetric'
  | 'totalCollectionViews'
  | 'totalProductViewMetric'
  | 'totalCheckoutStartedMetric'
  | 'totalSessionReachedCheckoutMetric'
  | 'totalSessionWithCartAdditionMetric'
  | 'totalSessionCompletedCheckoutMetric';

export type ICampaignAnalyticMetricDetect = Record<
  ICampaignAnalyticMetricDetectKey,
  IDetectMetricResult
>;

export interface ICampaignAnalyticMetricDetectResult extends ICampaignAnalyticMetricDetect {
  getMetricFormattedByKey: (metricKey: ICampaignAnalyticMetricDetectKey) => string;
}

type ICampaignAnalyticMetricDetectFunc = (
  props: ICampaignAnalyticMetricDetectProps,
) => ICampaignAnalyticMetricDetectResult;

export const useCampaignAnalyticMetricDetect: ICampaignAnalyticMetricDetectFunc = ({ metric }) => {
  const { getTextPrice } = useConvertMoneyByCurrency();

  const func = useCampaignAnalyticMetricFunc();

  const defaultMetric = func.getDefaultMetric({ metric });
  const totalRevenueMetric = func.getTotalRevenueMetric({ metric });
  const totalVisitorMetric = func.getTotalVisitorMetric({ metric });
  const totalCollectionViews = func.getTotalCollectionViews({ metric });
  const totalProductViewMetric = func.getTotalProductViewMetric({ metric });
  const totalATCMetric = func.getTotalATCMetric({ metric });
  const totalCheckoutStartedMetric = func.getTotalCheckoutStartedMetric({ metric });
  const avgTimeOnPageMetric = func.getAvgTimeOnPageMetric({ metric });
  const totalSessionMetric = func.getTotalSessionMetric({ metric });
  const conversionRateMetric = func.getConversionRateMetric({ metric });
  const atcRateMetric = func.getATCRateMetric({ metric });
  const aovMetric = func.getAovMetric({ metric });
  const rpvMetric = func.getRpvMetric({ metric });
  const totalOrderMetric = func.getTotalOrderMetric({ metric });
  const totalViewMetric = func.getTotalViewMetric({ metric });
  const linkCtrMetric = func.getLinkCtrMetric({ metric });
  const bounceRateMetric = func.getBounceRateMetric({ metric });
  const totalSessionWithCartAdditionMetric = func.getTotalSessionWithCartAdditionMetric({ metric });
  const totalSessionReachedCheckoutMetric = func.getTotalSessionReachedCheckoutMetric({ metric });
  const totalSessionCompletedCheckoutMetric = func.getTotalSessionCompletedCheckoutMetric({
    metric,
  });

  const dataMetric = useMemo(() => {
    return {
      totalRevenueMetric,
      totalVisitorMetric,
      totalCollectionViews,
      totalProductViewMetric,
      totalATCMetric,
      totalCheckoutStartedMetric,
      avgTimeOnPageMetric,
      totalSessionMetric,
      conversionRateMetric,
      totalViewMetric,
      atcRateMetric,
      aovMetric,
      rpvMetric,
      totalOrderMetric,
      linkCtrMetric,
      bounceRateMetric,
      defaultMetric,
      totalSessionWithCartAdditionMetric,
      totalSessionReachedCheckoutMetric,
      totalSessionCompletedCheckoutMetric,
    };
  }, [
    totalRevenueMetric,
    totalVisitorMetric,
    totalCollectionViews,
    totalProductViewMetric,
    totalATCMetric,
    totalCheckoutStartedMetric,
    avgTimeOnPageMetric,
    totalSessionMetric,
    conversionRateMetric,
    totalViewMetric,
    atcRateMetric,
    aovMetric,
    rpvMetric,
    totalOrderMetric,
    linkCtrMetric,
    bounceRateMetric,
    defaultMetric,
    totalSessionWithCartAdditionMetric,
    totalSessionReachedCheckoutMetric,
    totalSessionCompletedCheckoutMetric,
  ]);

  const getMetricFormattedByKey = useCallback(
    (metricKey: ICampaignAnalyticMetricDetectKey) => {
      return formatDataByDetailVariantMetricV2({
        metric: dataMetric,
        metricKey,
        getTextPrice,
      });
    },
    [dataMetric, getTextPrice],
  );

  return {
    ...dataMetric,
    getMetricFormattedByKey,
  };

  function formatDataByDetailVariantMetricV2({
    metric,
    metricKey,
    getTextPrice,
  }: {
    metric: ICampaignAnalyticMetricDetect;
    metricKey: keyof ICampaignAnalyticMetricDetect;
    getTextPrice: IGetTextPrice;
  }): string {
    const { formatter, value } = metric[metricKey];

    return formatMetricByFormatter({ value, formatter, getTextPrice });
  }
};
